import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { GFFrame, GFHeader } from "@/components/GFFrame";
import { INDUSTRIES, TEMPOH, GENDERS, AGE_RANGES, BUSINESS_ROLES, BUSINESS_MODELS, EMPLOYEE_COUNTS, REVENUE_RANGES } from "@/data/gfContent";

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <label className="text-sm font-semibold text-slate-600">{label}</label>
    {children}
  </div>
);

const inputCls =
  "w-full rounded-2xl border border-[#E7E2D6] bg-white px-4 py-3.5 text-[15px] text-[#0F172A] outline-none transition-colors focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20";

const BIZ_CHALLENGES = [
  "Jualan / aliran pelanggan", "Aliran tunai (cashflow)", "Margin keuntungan", "Pemasaran",
  "Jenama / positioning", "Pasukan / staf", "Operasi", "Penukaran pelanggan (conversion)",
  "Pertumbuhan / stagnasi", "Lain-lain",
];

export default function UserInfo() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nama: "",
    email: "",
    nama_bisnes: "",
    industri: "",
    tempoh_berniaga: "",
    gender: "",
    age_range: "",
    role: "",
    business_model: "",
    employees: "",
    revenue_range: "",
    revenue_current: "",
    revenue_best: "",
    revenue_target: "",
    operating_expenses: "",
    avg_transaction: "",
    main_product: "",
    biggest_challenge: "",
    improve_90d: "",
    show_financials_on_a4: false,
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = () => {
    const required = [
      "nama", "email", "nama_bisnes", "industri", "tempoh_berniaga",
      "gender", "age_range", "role", "business_model", "employees", "revenue_range",
    ];
    if (required.some((k) => !form[k])) {
      toast.error("Sila lengkapkan semua maklumat.");
      return;
    }
    const email = form.email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Sila masukkan alamat emel yang sah.");
      return;
    }
    sessionStorage.setItem("gf_user", JSON.stringify({ ...form, email }));
    localStorage.setItem("gf_ai_email", email);
    navigate("/soalan");
  };

  return (
    <GFFrame testId="userinfo-page">
      <div className="flex min-h-screen flex-col">
        <GFHeader />
        <button
          onClick={() => navigate("/")}
          data-testid="back-home-btn"
          className="ml-6 mt-2 flex w-fit items-center gap-1 text-sm text-slate-400"
        >
          <ArrowLeft className="h-4 w-4" /> Kembali
        </button>

        <div className="flex flex-1 flex-col px-6 pb-10 pt-4">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="gf-gold-line mb-5" />
            <h2 className="font-heading text-3xl font-bold leading-tight text-[#0F172A]">
              Ceritakan sedikit tentang bisnes anda
            </h2>
            <p className="mt-3 text-sm text-slate-500">
              Maklumat ini membantu kami menyesuaikan laporan Growth Factor anda.
              Tiada akaun diperlukan.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-8 space-y-5"
          >
            <Field label="Nama">
              <input
                data-testid="input-nama"
                className={inputCls}
                placeholder="Nama penuh anda"
                value={form.nama}
                onChange={set("nama")}
              />
            </Field>
            <Field label="Alamat Emel">
              <input
                data-testid="input-email"
                type="email"
                className={inputCls}
                placeholder="emel@contoh.com"
                value={form.email}
                onChange={set("email")}
              />
              <p className="text-xs leading-relaxed text-slate-400">
                Email diperlukan untuk menghantar laporan Business Growth dan hasil kempen Brand2Market anda.
              </p>
            </Field>
            <Field label="Nama Bisnes">
              <input
                data-testid="input-nama-bisnes"
                className={inputCls}
                placeholder="Nama syarikat / jenama"
                value={form.nama_bisnes}
                onChange={set("nama_bisnes")}
              />
            </Field>
            <Field label="Industri">
              <select
                data-testid="input-industri"
                className={inputCls}
                value={form.industri}
                onChange={set("industri")}
              >
                <option value="">Pilih industri</option>
                {INDUSTRIES.map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Tempoh Berniaga / Pengalaman">
              <select
                data-testid="input-tempoh"
                className={inputCls}
                value={form.tempoh_berniaga}
                onChange={set("tempoh_berniaga")}
              >
                <option value="">Pilih tempoh</option>
                {TEMPOH.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Model Bisnes">
              <select
                data-testid="input-business-model"
                className={inputCls}
                value={form.business_model}
                onChange={set("business_model")}
              >
                <option value="">Pilih model bisnes</option>
                {BUSINESS_MODELS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>

            <Field label="Peranan Anda">
              <select
                data-testid="input-role"
                className={inputCls}
                value={form.role}
                onChange={set("role")}
              >
                <option value="">Pilih peranan</option>
                {BUSINESS_ROLES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>

            <Field label="Bilangan Pekerja / Saiz Pasukan">
              <select
                data-testid="input-employees"
                className={inputCls}
                value={form.employees}
                onChange={set("employees")}
              >
                <option value="">Pilih saiz pasukan</option>
                {EMPLOYEE_COUNTS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>

            <Field label="Anggaran Hasil Bulanan">
              <select
                data-testid="input-revenue"
                className={inputCls}
                value={form.revenue_range}
                onChange={set("revenue_range")}
              >
                <option value="">Pilih julat hasil</option>
                {REVENUE_RANGES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Jantina">
                <select
                  data-testid="input-gender"
                  className={inputCls}
                  value={form.gender}
                  onChange={set("gender")}
                >
                  <option value="">Pilih</option>
                  {GENDERS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </Field>
              <Field label="Julat Umur">
                <select
                  data-testid="input-age"
                  className={inputCls}
                  value={form.age_range}
                  onChange={set("age_range")}
                >
                  <option value="">Pilih</option>
                  {AGE_RANGES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </Field>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-10"
            data-testid="kenali-perniagaan-section"
          >
            <div className="gf-gold-line mb-4" />
            <h3 className="font-heading text-xl font-bold text-[#0F172A]">Kenali Perniagaan</h3>
            <p className="mt-2 text-sm text-slate-500">
              Maklumat ringkas ini menjadikan laporan lebih peribadi. Ia tidak dinilai (tidak menjejaskan skor) —
              kongsi apa yang selesa sahaja.
            </p>

            <div className="mt-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Hasil Bulanan Semasa">
                  <input data-testid="input-revenue-current" className={inputCls}
                    placeholder="cth: RM38,000 atau julat" value={form.revenue_current} onChange={set("revenue_current")} />
                </Field>
                <Field label="Hasil Terbaik Dicapai">
                  <input data-testid="input-revenue-best" className={inputCls}
                    placeholder="cth: RM45,000" value={form.revenue_best} onChange={set("revenue_best")} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Sasaran Hasil (3–6 bulan)">
                  <input data-testid="input-revenue-target" className={inputCls}
                    placeholder="cth: RM60,000" value={form.revenue_target} onChange={set("revenue_target")} />
                </Field>
                <Field label="Anggaran Belanja Operasi">
                  <input data-testid="input-opex" className={inputCls}
                    placeholder="cth: RM20,000" value={form.operating_expenses} onChange={set("operating_expenses")} />
                </Field>
              </div>
              <Field label="Purata Nilai Satu Transaksi (pilihan)">
                <input data-testid="input-avg-transaction" className={inputCls}
                  placeholder="cth: RM50 — membolehkan pengiraan 'berapa pelanggan diperlukan'" value={form.avg_transaction} onChange={set("avg_transaction")} />
              </Field>
              <Field label="Produk / Perkhidmatan Utama Penjana Hasil">
                <input data-testid="input-main-product" className={inputCls}
                  placeholder="cth: Set nasi lemak premium" value={form.main_product} onChange={set("main_product")} />
              </Field>
              <Field label="Cabaran Perniagaan Terbesar Kini">
                <select data-testid="input-biggest-challenge" className={inputCls}
                  value={form.biggest_challenge} onChange={set("biggest_challenge")}>
                  <option value="">Pilih cabaran</option>
                  {BIZ_CHALLENGES.map((t) => (<option key={t} value={t}>{t}</option>))}
                </select>
              </Field>
              <Field label="Satu Perkara Paling Ingin Diperbaiki Dalam 90 Hari">
                <textarea data-testid="input-improve-90d" rows={2} className={inputCls}
                  placeholder="Tulis secara ringkas..." value={form.improve_90d} onChange={set("improve_90d")} />
              </Field>
            </div>
          </motion.div>

          <div className="mt-auto pt-8">
            <button
              data-testid="continue-to-assessment-btn"
              onClick={submit}
              className="group flex w-full items-center justify-center gap-3 rounded-full bg-[#0F172A] py-4 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98] hover:scale-[1.01]"
            >
              MULA PENILAIAN
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>
    </GFFrame>
  );
}
